import type { CSSProperties } from 'react';
import type { CmsCatalogRow } from 'app/services/cmsApi';

/**
 * The CMS Admin left-nav model — a 1:1 re-platform of `tmpl/main.menu.tmpl`
 * (administrative section, static) and `tmpl/product.menu.tmpl` (product
 * section, partly data-driven), as rendered by `view/main.menu.js` and
 * `view/product.menu.js`.
 *
 * ★ Verified against the LIVE nav on 2026-08-05: 67 anchors for mfg 1, in the
 * order produced below. Commented-out anchors in the legacy templates
 * (`permissions` landing, `numbering` parent, the old top-level `freight`) are
 * NOT reproduced — they do not render.
 */

export type CmsNavNode =
  /** A section label. `administrative` carries a `value` in legacy but no
   *  screen is wired to it, so clicking it is a no-op — reproduced as inert. */
  | { kind: 'heading'; label: string; icon: string; key?: string; extraClass?: string }
  /** A plain, always-visible link. */
  | { kind: 'item'; key: string; label: string; icon: string; dataType?: 'product'; itemClass?: string }
  /** A collapsible group. Legacy keeps at most ONE open at a time. */
  | {
      kind: 'group';
      /** `data-target` in legacy — also the group's DOM id. */
      id: string;
      label: string;
      icon: string;
      /** Legacy renders the `+`/`−` affordance only on some groups. */
      showToggleIcon: boolean;
      children: CmsNavChild[];
      /** `product-child` marks nodes appended by `product.menu.js`. */
      productChild?: boolean;
    };

export interface CmsNavChild {
  key: string;
  label: string;
  dataType?: 'product';
  itemClass?: string;
  /** Rendered as `<li class="non-clickable">` when the catalog disables it. */
  disabled?: boolean;
  /** `data-component-id` / `data-accessory-id` — the selection-change hook. */
  dataComponentId?: number;
  dataAccessoryId?: number;
  /**
   * `main.menu.tmpl` is INCONSISTENT about sub-item markup and the difference is
   * measurable, so it is modelled rather than normalised: the 7 `permissions_*`
   * rows and the 2 Numbering rows wrap their label in an extra
   * `<div class="">` (`main.menu.tmpl:41-100`), every other sub-item puts the
   * label straight into `.col-xs-12`. Measured on the live page: exactly 9 of
   * the 67 anchors have the inner div.
   */
  innerLabelDiv?: boolean;
  /**
   * Verbatim inline `style` attribute. Only `permissions_ahu` has one
   * (`main.menu.tmpl:76`, `style="text-transform:uppercase;"`) and it is
   * NOT cosmetic: the nav's `text-transform: capitalize` would otherwise render
   * the label as "Ahu" instead of "AHU".
   */
  inlineStyle?: CSSProperties;
}

/**
 * ADMINISTRATIVE section — `tmpl/main.menu.tmpl`, static markup.
 * Icon classes are the legacy `icon-*` glyph classes verbatim.
 */
export const CMS_ADMIN_NAV: CmsNavNode[] = [
  { kind: 'heading', label: 'Administrative', icon: 'icon-th-large', key: 'administrative' },
  { kind: 'item', key: 'manufacturer', label: 'manufacturer', icon: 'icon-mfg' },
  { kind: 'item', key: 'network', label: 'Mfg Network', icon: 'icon-mfg-network' },
  {
    kind: 'group',
    id: 'permissions',
    label: 'permissions',
    icon: 'icon-permissions',
    showToggleIcon: true,
    children: [
      { key: 'permissions_roles', label: 'roles', itemClass: 'permissions_sublist_item', innerLabelDiv: true },
      { key: 'permissions_access', label: 'access', itemClass: 'permissions_sublist_item', innerLabelDiv: true },
      { key: 'permissions_dashboard', label: 'dashboard', itemClass: 'permissions_sublist_item', innerLabelDiv: true },
      { key: 'permissions_project', label: 'project', itemClass: 'permissions_sublist_item', innerLabelDiv: true },
      // legacy adds an inline `text-transform:uppercase` on this one only
      {
        key: 'permissions_ahu',
        label: 'ahu',
        itemClass: 'permissions_sublist_item',
        innerLabelDiv: true,
        // `main.menu.tmpl:76` — renders "AHU", not "Ahu".
        inlineStyle: { textTransform: 'uppercase' },
      },
      { key: 'permissions_reports', label: 'reports', itemClass: 'permissions_sublist_item', innerLabelDiv: true },
      { key: 'permissions_workflow', label: 'workflow', itemClass: 'permissions_sublist_item', innerLabelDiv: true },
    ],
  },
  {
    kind: 'group',
    id: 'menunumbering',
    label: 'Numbering',
    icon: 'icon-numbering',
    showToggleIcon: true,
    children: [
      { key: 'quote_format', label: 'Quote Number', itemClass: 'numbering_sublist_item', innerLabelDiv: true },
      { key: 'job_format', label: 'Job Number', itemClass: 'numbering_sublist_item', innerLabelDiv: true },
    ],
  },
  { kind: 'item', key: 'workflow', label: 'workflow', icon: 'icon-workflow' },
  {
    kind: 'group',
    id: 'costing',
    label: 'Costing',
    icon: 'icon-cost',
    showToggleIcon: true,
    children: [
      { key: 'costs', label: 'Costs', itemClass: 'cost_sublist_item' },
      { key: 'activities', label: 'Activities', itemClass: 'cost_sublist_item' },
      { key: 'allocations', label: 'Allocations', itemClass: 'cost_sublist_item' },
      { key: 'multipliers', label: 'Multipliers', itemClass: 'cost_sublist_item' },
      { key: 'freight', label: 'Freight', itemClass: 'cost_sublist_item' },
    ],
  },
  { kind: 'item', key: 'document', label: 'Documents', icon: 'icon-documents' },
  { kind: 'heading', label: 'Product', icon: 'icon-th-large', key: 'products' },
];

/**
 * The static half of the PRODUCT section — `tmpl/product.menu.tmpl`, everything
 * except the two `{{#each}}` groups. `product.menu.js:render()` appends all of
 * this into `#main_menu nav`, NOT into `#product_menues` (which stays empty —
 * measured: `productMenuesHtmlLen: 0`).
 *
 * ★ Legacy marks the Modular Frame button `non-clickable` `{{#unless is_modular}}`,
 * and `render()` passes `is_modular: true` unconditionally — so it is never
 * disabled in practice. Reproduced as always-enabled.
 */
export const CMS_PRODUCT_NAV_STATIC = {
  settings: {
    id: 'settings',
    label: 'settings',
    children: [
      { key: 'settProduct', label: 'Product' },
      { key: 'settDynamicSizer', label: 'Dynamic Sizer' },
      { key: 'settOptions', label: 'Options' },
      { key: 'settModelView', label: 'Model . View' },
      { key: 'settManufacturing', label: 'Manufacturing' },
      { key: 'settWeight', label: 'Weight' },
      { key: 'settCost', label: 'Cost' },
    ],
    itemClass: 'sett_sublist_item',
    showToggleIcon: true,
  },
  construction: {
    id: 'construction',
    label: 'construction',
    children: [
      { key: 'cnstrSettings', label: 'Class' },
      { key: 'rawMaterial', label: 'Raw Materials' },
      { key: 'cabinet', label: 'Cabinet' },
      { key: 'base', label: 'Base' },
      { key: 'lugs', label: 'Lug' },
      // ★ NO `roof_curb` — the legacy <ul> has exactly these five anchors.
    ],
    itemClass: 'cnstr_sublist_item',
    showToggleIcon: true,
  },
  modular_frame: {
    id: 'product_modular_frame',
    label: 'Modular Frame',
    children: [
      { key: 'mf_parts', label: 'parts' },
      { key: 'mf_sett', label: 'settings' },
      { key: 'mf_rules', label: 'rules' },
    ],
    itemClass: 'modular_frame_sublist_item',
    // ★ legacy omits the `+` icon on this ONE group only (product.menu.tmpl:105-110)
    showToggleIcon: false,
  },
} as const;

/**
 * Component nav rows — `tmpl/product.menu.tmpl:143-165`, `{{#each components}}`.
 *
 * The anchor `value` is the catalog `value` with an `s` appended, EXCEPT
 * `er_wheel` and `split_plenum`, which are used verbatim. (The template also
 * has a `plate_heat_exchanger` branch, but both of its arms emit `{{value}}s`,
 * so it is a no-op — reproduced as such.)
 * `plenum` and `user_defined_component` are filtered OUT entirely.
 */
const COMPONENT_VERBATIM = new Set(['er_wheel', 'split_plenum']);
const COMPONENT_EXCLUDED = new Set(['plenum', 'user_defined_component']);

export const buildComponentNavChildren = (
  catalog: CmsCatalogRow[],
  availableComponentIds: number[]
): CmsNavChild[] =>
  catalog
    .filter((row) => !COMPONENT_EXCLUDED.has(row.value))
    .map((row) => ({
      key: COMPONENT_VERBATIM.has(row.value) ? row.value : `${row.value}s`,
      label: row.name,
      dataType: 'product' as const,
      itemClass: 'cmp_sublist_item',
      dataComponentId: row.component_id,
      // `{{#ifNotIn component_id ../available_components}}` — legacy seeds the
      // available list with a literal `[100]` and then fills it from the SAME
      // collection it renders, so at boot nothing is ever disabled. Kept
      // faithful (and correct) rather than hard-coding "never disabled": the
      // `components:selection:change` event flips these at runtime.
      disabled:
        row.component_id !== undefined &&
        !availableComponentIds.includes(row.component_id),
    }));

/**
 * Accessory nav rows — `tmpl/product.menu.tmpl:170-197`.
 *
 * Six explicit non-pluralising branches, then a default that appends `s`:
 *   adp_gage · jbox · disconnect · enclosure · vfd · starter · light ·
 *   drain_pan · user_defined_accessory  → verbatim
 *   uvc_system                          → renamed to `uvc`
 *   everything else                     → `<value>s`
 * Then two HARD-CODED anchors that are not catalog-driven at all:
 * `viewport` and `piping_accessorie`.
 */
const ACCESSORY_VERBATIM = new Set([
  'adp_gage',
  'jbox',
  'disconnect',
  'enclosure',
  'vfd',
  'starter',
  'light',
  'drain_pan',
  'user_defined_accessory',
]);

export const buildAccessoryNavChildren = (catalog: CmsCatalogRow[]): CmsNavChild[] => {
  const rows: CmsNavChild[] = catalog.map((row) => {
    let key: string;
    if (row.value === 'uvc_system') key = 'uvc';
    else if (ACCESSORY_VERBATIM.has(row.value)) key = row.value;
    else key = `${row.value}s`;
    return {
      key,
      label: row.name,
      dataType: 'product' as const,
      itemClass: 'cmp_sublist_item',
      dataAccessoryId: row.accessory_id,
    };
  });
  // The two hard-coded tail anchors (`product.menu.tmpl:199-212`).
  rows.push({ key: 'viewport', label: 'viewport', dataType: 'product', itemClass: 'acs_sublist_item' });
  rows.push({
    key: 'piping_accessorie',
    label: 'Piping accessorie',
    dataType: 'product',
    itemClass: 'acs_sublist_item',
  });
  return rows;
};

/** Trailing product-section items — `product.menu.tmpl:214-238`. */
export const CMS_PRODUCT_NAV_TAIL: CmsNavNode[] = [
  {
    kind: 'item',
    key: 'special_options',
    label: 'Special Options',
    icon: 'icon-folder-open-24px',
    dataType: 'product',
  },
  {
    kind: 'heading',
    label: 'Artificial Intelligence',
    icon: 'icon-th-large',
    extraClass: 'kinetix-ai-menu-item',
  },
  {
    kind: 'item',
    key: 'kinetix_ai_knowledge_base',
    label: 'Knowledge Base',
    icon: 'icon-folder-open-24px',
    itemClass: 'kinetix_ai_sublist_item',
  },
  {
    kind: 'item',
    key: 'kinetix_ai_insights',
    label: 'AI Insights',
    icon: 'icon-folder-open-24px',
    itemClass: 'kinetix_ai_sublist_item',
  },
  {
    kind: 'item',
    key: 'kinetix_ai_usage_statistics',
    label: 'Usage Statistics',
    icon: 'icon-folder-open-24px',
    itemClass: 'kinetix_ai_sublist_item',
  },
  {
    kind: 'item',
    key: 'kinetix_ai_release_notes',
    label: 'Release Notes',
    icon: 'icon-folder-open-24px',
    itemClass: 'kinetix_ai_sublist_item',
  },
];
